// NEW: UI_REVAMP
import { Box, Flex, Text } from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ImgComp from '../../utils/ImgComp';
import Loading from '../../utils/Loading';
import Time, { ConvertedTime } from '../../utils/Time';
import { currencyFormat } from '../../utils/numberFormat';
import VoidButton from './VoidButton';
import HistoryBetAccordionList, {
	groupHistoryBetsByGameAndType,
} from './detail/HistoryBetAccordionList';
import HistoryBetSectionHeader from './detail/HistoryBetSectionHeader';
import HistoryTicketInfoCard from './detail/HistoryTicketInfoCard';

const HISTORY_RESET_FILTERS_ON_RETURN_KEY = 'historyResetFiltersOnReturn';

const HistoryDetail = () => {
	const [data, setData] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const { serverTime } = Time();

	const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/bet/ticket/${sessionStorage.getItem(
		'accessTicketId',
	)}`;
	const authToken = sessionStorage.getItem('accessToken');

	const fetchTicketApi = useCallback(() => {
		axios
			.get(apiUrl, {
				headers: {
					Authorization: `Bearer ${authToken}`,
				},
			})
			.then((response) => {
				setData(response.data.data);
				setIsLoading(false);
			})
			.catch(() => {});
	}, [apiUrl, authToken]);

	useEffect(() => {
		fetchTicketApi();
	}, [fetchTicketApi]);

	const ticket = data?.[0];
	const bets = useMemo(() => ticket?.betting || [], [ticket]);
	const successfulBets = useMemo(() => bets.filter((bet) => bet.isFail === false), [bets]);
	const failedBets = useMemo(() => bets.filter((bet) => bet.isFail === true), [bets]);

	if (isLoading) {
		return (
			<Flex height='100vh' justifyContent='center' alignItems='center' width='100vw'>
				<Loading />
			</Flex>
		);
	}

	const totalBetCount = bets.length;
	const totalBetLabel = totalBetCount > 1 ? `${totalBetCount} bets` : `${totalBetCount} bet`;
	const totalWinsLabel = `${ticket?.winningCount || 0}/${totalBetCount} wins`;
	const totalBetAmount = currencyFormat(Number(ticket?.totalBetPrice || 0));
	const totalWinAmount = currencyFormat(Number(ticket?.winningPrice || 0));
	const successfulBetGroups = groupHistoryBetsByGameAndType(successfulBets);
	const failedBetGroups = groupHistoryBetsByGameAndType(failedBets);
	const markHistoryFiltersForReset = () => {
		sessionStorage.setItem(HISTORY_RESET_FILTERS_ON_RETURN_KEY, 'true');
	};

	return (
		<Box
			width='100%'
			backgroundColor={{ base: '#FFFFFF', lg: 'transparent' }}
			borderRadius={{ base: '16px', lg: '0' }}
			height={{ base: 'calc(100dvh - 88px)', lg: 'auto' }}
			overflowX='hidden'
			overflowY={{ base: 'auto', lg: 'visible' }}
			paddingBottom={{ base: '132px', lg: '0' }}
			sx={{ WebkitOverflowScrolling: 'touch' }}
			marginTop={{ base: '5px', lg: '0' }}
		>
			<Flex
				display={{ base: 'none', lg: 'flex' }}
				justifyContent='space-between'
				alignItems='center'
				marginBottom={['10px', '10px', '12px', '12px', '14px']}
				gap='12px'
			>
				<Link to='/history' onClick={markHistoryFiltersForReset}>
					<Flex alignItems='center' gap='8px'>
						<Box width='12px' height='12px' color='#545454' transform='rotate(180deg)'>
							<ImgComp svgName='chevron-right' width='12px' height='12px' />
						</Box>
						<Text fontSize='16px' fontWeight='500' lineHeight='100%' color='#111111'>
							Back
						</Text>
					</Flex>
				</Link>
			</Flex>

			{ticket ? (
				<Box padding={{ base: '0 12px', lg: '0' }}>
					<Flex
						display={{ base: 'flex', lg: 'none' }}
						alignItems='center'
						justifyContent='space-between'
						backgroundColor='#FFFFFF'
						padding='12px 4px 10px'
						marginBottom='8px'
					>
						<Text fontSize='16px' fontWeight='500' lineHeight='100%' color='#111111'>
							Ticket
						</Text>
						<Link to='/history' onClick={markHistoryFiltersForReset}>
							<Flex alignItems='center' justifyContent='center' color='#111111'>
								<CloseIcon boxSize='14px' />
							</Flex>
						</Link>
					</Flex>

					<HistoryTicketInfoCard
						ticketId={ticket.ticketNumber}
						betDateTime={ConvertedTime({ time: ticket.date, format: 'DD/MM/YYYY hh:mm A' })}
						drawDateTime={ConvertedTime({ time: ticket.drawTime, format: 'DD/MM/YYYY hh:mm A' })}
						siteType={ticket.siteName}
						totalBets={totalBetLabel}
						totalBetAmount={totalBetAmount}
						totalWins={totalWinsLabel}
						totalWinAmount={totalWinAmount}
						ticketStatus={ticket.status}
						isExpire={ticket.isExpire}
						ticket={ticket}
						serverTime={serverTime}
					/>
				</Box>
			) : null}

			<Box padding={{ base: '0 12px', lg: '0' }}>
				{successfulBets.length > 0 ? (
					<>
						<HistoryBetSectionHeader success numOfList={successfulBets.length} />
						<HistoryBetAccordionList groups={successfulBetGroups} showText />
					</>
				) : null}

				{failedBets.length > 0 ? (
					<>
						<HistoryBetSectionHeader success={false} numOfList={failedBets.length} />
						<HistoryBetAccordionList groups={failedBetGroups} showText={false} />
					</>
				) : null}
			</Box>

			{!ticket?.isDownline ? <VoidButton /> : null}
		</Box>
	);
};

export default HistoryDetail;
